import {
	PropsWithChildren,
	ReactElement,
	useContext
} from "react";
import classNames from "classnames";
import { setTrackingId, generateTrackingId } from "client/Misc/utils";
import { ValidationError } from "../ValidationError/ValidationError";
import styles from "./ReorderableList.module.less";
import {
	ControlAttachmentContext,
	ControlAttachmentContextData
} from "../ControlAttachment";
import { ReorderableListItemContext } from "./ReorderableListItemContext";
import { IconButton } from "../IconButton/IconButton";
import { ReorderableListItemBase } from "./ReorderableListItemBase";

export type ReorderableListPanelItemProps = {
	header: ReactElement;
	validationMessage?: string;
	trackingId?: string;
	onRemove?(): void;
};

const headerInputAttachmentsContext: ControlAttachmentContextData = {
	attachedOn: ["right", "bottom"]
};

export const ReorderableListPanelItem = (props: PropsWithChildren<ReorderableListPanelItemProps>): ReactElement => {
	const {
		header,
		onRemove,
		trackingId,
		validationMessage,
		children
	} = props;

	const isRemovable: boolean = Boolean(onRemove);

	const {
		onUp,
		onDown
	} = useContext(ReorderableListItemContext);

	return (
		<ReorderableListItemBase
			trackingId={trackingId}
		>
			<div
				{...setTrackingId(trackingId, "__content")}
				className={styles.panelItem}
			>
				<div
					className={classNames(
						styles.panelItemHeader,
						{
							[styles.panelItemHeader_fullSize]: !isRemovable
						}
					)}
				>
					<ControlAttachmentContext.Provider value={headerInputAttachmentsContext}>
						{header}
					</ControlAttachmentContext.Provider>
				</div>
				{isRemovable && (
					<div className={styles.panelItemRemoveButton}>
						<IconButton
							kind="secondary"
							icon="minus"
							onClick={onRemove}
							attachedOn={["left", "bottom"]}
							trackingId={generateTrackingId(trackingId, "__remove-button")}
						/>
					</div>
				)}
				<div className={styles.panelItemContent}>
					{children}
				</div>
				<div className={styles.panelItemOrder}>
					<IconButton
						kind="transparent"
						icon="arrow-up"
						onClick={onUp}
						isPlaceholder={!onUp}
						trackingId={generateTrackingId(trackingId, "__move-up")}
					/>
					<IconButton
						kind="transparent"
						icon="arrow-down"
						onClick={onDown}
						isPlaceholder={!onDown}
						trackingId={generateTrackingId(trackingId, "__move-down")}
					/>
				</div>
			</div>
			<ValidationError
				message={validationMessage}
				trackingId={generateTrackingId(trackingId, "__validation-error")}
			/>
		</ReorderableListItemBase>
	);
};

ReorderableListPanelItem.displayName = "ReorderableList.PanelItem";
